/**
 * React Query hooks for Offering Memorandums
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as omService from '@/services/offering-memorandum.service';
import { logActivity } from '@/services/activity.service';
import type { OMFilters } from '@/services/offering-memorandum.service';

// Query key factory
export const omKeys = {
  all: ['offering-memorandums'] as const,
  lists: () => [...omKeys.all, 'list'] as const,
  list: (filters?: OMFilters, page?: number) => [...omKeys.lists(), { filters, page }] as const,
  details: () => [...omKeys.all, 'detail'] as const,
  detail: (id: string) => [...omKeys.details(), id] as const,
  counts: () => [...omKeys.all, 'counts'] as const,
};

// Re-export types for convenience
export type { OfferingMemorandum, OMFilters, OMFile, OMFileCategory, OMFileCounts } from '@/services/offering-memorandum.service';

// Query key factory for OM files (per-category file management)
export const omFileKeys = {
  all: ['om-files'] as const,
  forOM: (omId: string) => [...omFileKeys.all, omId] as const,
  counts: (omId: string) => [...omFileKeys.all, 'counts', omId] as const,
};

/**
 * Fetch paginated offering memorandums with filters
 */
export function useOfferingMemorandums(filters?: OMFilters, page = 1, pageSize = 25) {
  return useQuery({
    queryKey: omKeys.list(filters, page),
    queryFn: () => omService.getOfferingMemorandums(filters, page, pageSize),
    staleTime: 30 * 1000,
  });
}

/**
 * Get status counts
 */
export function useOMCounts() {
  return useQuery({
    queryKey: omKeys.counts(),
    queryFn: () => omService.getOMCountsByStatus(),
    staleTime: 30 * 1000,
  });
}

/**
 * Create a new prospect (without uploading files)
 */
export function useCreateProspect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyType,
      userId,
      userName,
      userEmail,
      propertyName,
      notes,
    }: {
      propertyType: 'mhc' | 'self_storage';
      userId: string;
      userName: string;
      userEmail: string;
      propertyName?: string;
      notes?: string;
    }) => {
      return omService.createProspect(
        propertyType,
        userId,
        userName,
        userEmail,
        propertyName,
        notes
      );
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: omKeys.lists() });
      queryClient.invalidateQueries({ queryKey: omKeys.counts() });

      logActivity({
        activity_type: 'document_uploaded',
        entity_type: 'document',
        entity_id: data.id,
        entity_name: data.property_name || 'New Prospect',
        property_id: null,
        property_name: null,
        deal_id: null,
        deal_name: null,
        description: `created new prospect "${data.property_name || 'New Prospect'}" (${variables.propertyType === 'mhc' ? 'MHC' : 'Self Storage'})`,
        details: {
          property_type: variables.propertyType,
          property_name: variables.propertyName,
        },
        user_id: variables.userId,
        user_name: variables.userName,
        user_email: variables.userEmail,
      }).catch(console.error);
    },
  });
}

/**
 * Update OM status
 */
export function useUpdateOMStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      userId,
      userName,
    }: {
      id: string;
      status: 'new' | 'reviewing' | 'pass';
      userId: string;
      userName: string;
      userEmail?: string;
      omName?: string;
    }) => {
      return omService.updateOMStatus(id, status, userId, userName);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: omKeys.lists() });
      queryClient.invalidateQueries({ queryKey: omKeys.counts() });
      queryClient.invalidateQueries({ queryKey: omKeys.detail(variables.id) });

      // Log activity
      const statusLabels: Record<string, string> = {
        new: 'New',
        reviewing: 'Reviewing',
        pass: 'Pass',
      };

      logActivity({
        activity_type: 'document_uploaded',
        entity_type: 'document',
        entity_id: data.id,
        entity_name: data.file_name,
        property_id: null,
        property_name: null,
        deal_id: null,
        deal_name: null,
        description: `changed OM status of "${data.file_name}" to ${statusLabels[variables.status]}`,
        details: {
          new_status: variables.status,
          file_name: data.file_name,
        },
        user_id: variables.userId,
        user_name: variables.userName,
        user_email: variables.userEmail || '',
      }).catch(console.error);
    },
  });
}

/**
 * Delete an OM (permanent delete - admin only)
 */
export function useDeleteOM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => omService.deleteOfferingMemorandum(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: omKeys.lists() });
      queryClient.invalidateQueries({ queryKey: omKeys.counts() });
    },
  });
}

/**
 * Archive a prospect (soft delete)
 */
export function useArchiveOM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => omService.archiveOM(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: omKeys.lists() });
      queryClient.invalidateQueries({ queryKey: omKeys.counts() });
    },
  });
}

/**
 * Unarchive (restore) a prospect
 */
export function useUnarchiveOM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => omService.unarchiveOM(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: omKeys.lists() });
      queryClient.invalidateQueries({ queryKey: omKeys.counts() });
    },
  });
}

// =============================================================================
// OM FILE HOOKS (per-category file management)
// =============================================================================

/**
 * Fetch files for an OM
 */
export function useOMFiles(omId?: string) {
  return useQuery({
    queryKey: omFileKeys.forOM(omId || ''),
    queryFn: () => omService.getOMFiles(omId!),
    enabled: !!omId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch file counts per category for an OM
 */
export function useOMFileCounts(omId?: string) {
  return useQuery({
    queryKey: omFileKeys.counts(omId || ''),
    queryFn: () => omService.getOMFileCounts(omId!),
    enabled: !!omId,
    staleTime: 30 * 1000,
  });
}

/**
 * Upload a file to a specific category for an OM
 */
export function useUploadOMFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      omId,
      category,
      userId,
      userName,
      userEmail,
      relativePath,
    }: {
      file: File;
      omId: string;
      category: omService.OMFileCategory;
      userId: string;
      userName: string;
      userEmail: string;
      relativePath?: string;
    }) => {
      return omService.uploadOMFile(file, omId, category, userId, userName, userEmail, relativePath);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: omFileKeys.forOM(variables.omId) });
      queryClient.invalidateQueries({ queryKey: omFileKeys.counts(variables.omId) });

      const categoryLabels: Record<string, string> = {
        om: 'OM',
        nda: 'NDA',
        project_files: 'Project File',
      };

      logActivity({
        activity_type: 'document_uploaded',
        entity_type: 'document',
        entity_id: data.id,
        entity_name: data.file_name,
        property_id: null,
        property_name: null,
        deal_id: null,
        deal_name: null,
        description: `uploaded ${categoryLabels[variables.category]} "${data.file_name}"`,
        details: {
          file_name: data.file_name,
          file_size: data.file_size,
          category: variables.category,
          offering_memorandum_id: variables.omId,
        },
        user_id: variables.userId,
        user_name: variables.userName,
        user_email: variables.userEmail,
      }).catch(console.error);
    },
  });
}

/**
 * Rename an OM file
 */
export function useRenameOMFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fileId, newFileName }: { fileId: string; newFileName: string; omId: string }) =>
      omService.renameOMFile(fileId, newFileName),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: omFileKeys.forOM(variables.omId) });
    },
  });
}

/**
 * Toggle the "is_offering_memorandum" flag on a file
 */
export function useToggleOMFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fileId, isOM }: { fileId: string; isOM: boolean; omId: string }) =>
      omService.toggleOMFileFlag(fileId, isOM),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: omFileKeys.forOM(variables.omId) });
    },
  });
}

/**
 * Delete an OM file record
 */
export function useDeleteOMFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fileId }: { fileId: string; omId: string }) =>
      omService.deleteOMFile(fileId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: omFileKeys.forOM(variables.omId) });
      queryClient.invalidateQueries({ queryKey: omFileKeys.counts(variables.omId) });
    },
  });
}

/**
 * Retry SharePoint sync for a file that failed to upload
 */
export function useRetrySyncOMFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, fileContent, omId }: { file: omService.OMFile; fileContent: File; omId: string }) =>
      omService.retrySyncOMFile(file, fileContent, omId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: omFileKeys.forOM(variables.omId) });
    },
  });
}

// =============================================================================
// OM–PROPERTY LINKING HOOKS
// =============================================================================

export const omPropertyKeys = {
  all: ['om-properties'] as const,
  forOM: (omId: string) => [...omPropertyKeys.all, 'om', omId] as const,
  forProperty: (propertyId: string) => [...omPropertyKeys.all, 'property', propertyId] as const,
};

/**
 * Fetch properties linked to an OM
 */
export function useLinkedProperties(omId?: string) {
  return useQuery({
    queryKey: omPropertyKeys.forOM(omId || ''),
    queryFn: () => omService.getLinkedProperties(omId!),
    enabled: !!omId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch OMs linked to a property
 */
export function usePropertyOMs(propertyId?: string) {
  return useQuery({
    queryKey: omPropertyKeys.forProperty(propertyId || ''),
    queryFn: () => omService.getPropertyOMs(propertyId!),
    enabled: !!propertyId,
    staleTime: 30 * 1000,
  });
}

/**
 * Set property links for an OM (replaces all existing links)
 */
export function useSetOMPropertyLinks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      omId,
      propertyLinks,
      linkedBy,
    }: {
      omId: string;
      propertyLinks: omService.PropertyLinkInput[];
      linkedBy?: string;
    }) => omService.setOMPropertyLinks(omId, propertyLinks, linkedBy),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: omPropertyKeys.forOM(variables.omId) });
      variables.propertyLinks.forEach((link) => {
        queryClient.invalidateQueries({ queryKey: omPropertyKeys.forProperty(link.id) });
      });
    },
  });
}
